import { cx, css } from '@mildang/styled-system/css';
import { splitCssProps } from '@mildang/styled-system/jsx';
import {
  type TypographyVariantProps,
  typography,
  type TypographyVariant,
} from '@mildang/styled-system/recipes';
import type { HTMLStyledProps } from '@mildang/styled-system/types';
import { ComponentPropsWithRef, ElementType, forwardRef, ReactNode } from 'react';

type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any ? Omit<T, TOmitted> : never;

export type TypographyProps<TAs extends ElementType = 'span'> = TypographyVariantProps &
  DistributiveOmit<ComponentPropsWithRef<ElementType extends TAs ? 'p' : TAs>, 'as'> & {
    as?: TAs;
  } & HTMLStyledProps<TAs>;

const Text = forwardRef<{}, TypographyProps<'p'>>(function Text(props, ref) {
  const [cssProps, restProps] = splitCssProps(props);
  const { css: cssProp, ...styleProps } = cssProps;
  const [variantProps, localProps] = typography.splitVariantProps(restProps);

  const {
    as: AsComponent = 'p',
    className: classNameProp,
    ...others
  } = localProps as unknown as Omit<TypographyProps<'p'>, keyof TypographyVariant>;

  const Component = AsComponent as ElementType;

  const className = css(styleProps, cssProp);

  return (
    <Component ref={ref} className={cx(typography(variantProps), className, classNameProp)} {...others} />
  );
});

type TextType<TAs extends ElementType = 'span'> = (props: TypographyProps<TAs>) => ReactNode;

export default Text as unknown as TextType;
